import { useNavigate } from 'react-router-dom';
import { PlusIcon, ChatIcon } from '../../icons';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';

interface Conversation {
  id: string;
  name: string | null;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  last_activity_at: string;
}

interface ConversationListPageProps {
  conversations: Conversation[];
  loading: boolean;
  creating: boolean;
  error: string | null;
  onCreateConversation: () => Promise<void>;
  onBack: () => void;
}

export function ConversationListPage({
  conversations,
  loading,
  creating,
  error,
  onCreateConversation,
  onBack,
}: ConversationListPageProps) {
  const navigate = useNavigate();

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCreateAndNavigate = async () => {
    try {
      await onCreateConversation();
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-testid="conversations-list-view"
    >
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={onBack}
                className="px-3 py-2 text-slate-300 hover:text-white transition-colors text-sm sm:text-base"
                data-testid="back-button"
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Conversations</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-4">
        {/* Create Button */}
        <div className="mb-4">
          <Button
            onClick={handleCreateAndNavigate}
            variant="primary"
            size="lg"
            icon={creating ? undefined : <PlusIcon />}
            loading={creating}
            className="w-full shadow-lg hover:shadow-xl transition-shadow"
            data-testid="new-conversation-button"
          >
            {creating ? 'Creating...' : 'New Conversation'}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Conversations List */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Spinner size="lg" />
              <p className="text-slate-400 mt-2">Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-5xl mb-4">💬</div>
              <h3 className="text-lg font-semibold text-white mb-2">No conversations yet</h3>
              <p className="text-slate-400">Create your first conversation to get started!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => navigate(`/conversations/${conversation.id}`)}
                  className="w-full p-4 text-left hover:bg-slate-700/50 transition-colors flex items-start gap-3"
                  data-testid={`conversation-item-${conversation.id}`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <ChatIcon className="w-5 h-5 text-slate-300" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-white truncate">
                        {conversation.name || 'Untitled'}
                      </h3>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {formatRelativeTime(conversation.last_activity_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 truncate mt-0.5">Tap to view messages</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
